<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import UnifiedSidebar from '~/components/UnifiedSidebar.vue'
import type { SidebarItem } from '~/components/UnifiedSidebar.vue'
import ImageViewer from '~/components/ImageViewer.vue'
import BlurhashImage from '~/components/BlurhashImage.vue'
import { getThumbUrl, getFullImageUrl, preloadImage, isImageCached } from '~/composables/useImageViewer'
import type { GalleryImage, ImageCategory } from '~/composables/useGalleryApi'

// 使用通用侧边栏状态
const { isOpen: isSidebarOpen, close: closeSidebar } = useSidebar('gallery')
const config = useRuntimeConfig()
const apiBase = config.public.apiBase || 'https://image.lixiang.page'

/**
 * 1. 【性能优化】带缓存的数据获取
 * 使用 useState 做全局缓存，缓存 30 分钟避免频繁请求
 */
const CACHE_DURATION = 30 * 60 * 1000 // 30 分钟

interface CachedData<T> {
  data: T
  timestamp: number
}

// 全局缓存状态
const categoriesCache = useState<CachedData<{ categories: ImageCategory[] }> | null>('gallery-categories-cache', () => null)
const imagesCache = useState<CachedData<{ images: GalleryImage[]; total: number }> | null>('gallery-images-cache', () => null)

// 检查缓存是否有效
const isCacheValid = <T>(cache: CachedData<T> | null): cache is CachedData<T> => {
  if (!cache) return false
  return Date.now() - cache.timestamp < CACHE_DURATION
}

// 获取分类数据
const { data: categoriesData, status: categoriesStatus } = await useAsyncData('gallery-categories', async () => {
  // 检查缓存
  if (isCacheValid(categoriesCache.value)) {
    return categoriesCache.value.data
  }
  
  // 请求新数据
  const result = await $fetch<{ categories: ImageCategory[] }>(`${apiBase}/image-categories`, {
    query: { publicOnly: '1' }
  })
  
  // 更新缓存
  categoriesCache.value = {
    data: result,
    timestamp: Date.now()
  }
  
  return result
}, { server: true, dedupe: 'defer' })

// 获取图片数据
const { data: imagesData, status: imagesStatus } = await useAsyncData('gallery-images', async () => {
  // 检查缓存
  if (isCacheValid(imagesCache.value)) {
    return imagesCache.value.data
  }
  
  // 请求新数据
  const result = await $fetch<{ images: GalleryImage[]; total: number }>(`${apiBase}/images`, {
    query: { publicOnly: '1' }
  })
  
  // 更新缓存
  imagesCache.value = {
    data: result,
    timestamp: Date.now()
  }
  
  return result
}, { server: true, dedupe: 'defer' })

// 获取 Banner 数据
interface PageBanner {
  BannerId: number
  PageName: string
  Title: string | null
  Subtitle: string | null
  CoverImage: string | null
  Location: string | null
  Link: string | null
}

const bannerCache = useState<CachedData<PageBanner | null> | null>('gallery-banner-cache', () => null)

const { data: bannerData } = await useAsyncData('gallery-banner', async () => {
  // 检查缓存
  if (isCacheValid(bannerCache.value)) {
    return bannerCache.value.data
  }

  try {
    const res = await $fetch<{ banner: PageBanner }>(`${apiBase}/page-banners/page/gallery`)
    
    // 更新缓存
    bannerCache.value = {
      data: res.banner,
      timestamp: Date.now()
    }
    
    return res.banner
  } catch (e) {
    // 忽略错误，使用默认
    return null
  }
}, { server: true, dedupe: 'defer' })

const banner = computed(() => {
  return bannerData.value || {
    Title: '图集',
    Subtitle: '用镜头记录世界的美好',
    CoverImage: null,
    Location: null,
    Link: null
  }
})

const bannerImage = computed(() => {
  if (banner.value.CoverImage) {
    return getFullImageUrl(banner.value.CoverImage)
  }
  // 默认封面
  return 'https://picsum.photos/id/29/1280/400'
})

/**
 * 2. 【分类与分组】
 */
const categories = computed(() => {
  return categoriesData.value?.categories?.map(cat => cat.Name) || []
})

const wordCount = computed(() => {
  return categoriesData.value?.categories?.reduce((sum, cat) => sum + (cat.Name.length || 0), 0) || 0
})

const imagesByCategory = computed(() => {
  const grouped: Record<string, GalleryImage[]> = {}
  const images = imagesData.value?.images || []
  const rawCats = categoriesData.value?.categories || []

  rawCats.forEach(cat => {
    grouped[cat.Name] = []
  })

  const idToNameMap = new Map(rawCats.map(c => [c.CategoryId, c.Name]))
  images.forEach(img => {
    if (img.CategoryId !== null) {
      const name = idToNameMap.get(img.CategoryId)
      if (name && grouped[name]) {
        grouped[name].push(img)
      }
    }
  })
  return grouped
})

/**
 * 3. 【全部图片扁平列表】用于 Viewer 导航
 * 按照画廊分类显示顺序排列，确保 Viewer 中的顺序与画廊一致
 */
const allImages = computed(() => {
  const result: Array<{
    ImageId: number
    Key: string
    Title: string | null
    Description: string | null
    BlurHash: string | null
    ExifData: string | null
    CreatedAt: string
  }> = []
  
  // 按分类顺序遍历，保持与画廊显示一致
  for (const category of categories.value) {
    const categoryImages = imagesByCategory.value[category] || []
    for (const img of categoryImages) {
      result.push({
        ImageId: img.ImageId,
        Key: img.Key,
        Title: img.Title,
        Description: img.Description,
        BlurHash: img.BlurHash,
        ExifData: img.ExifData,
        CreatedAt: img.CreatedAt
      })
    }
  }
  
  return result
})

/**
 * 4. 【图片索引映射】快速查找图片在全列表中的位置
 */
const imageIndexMap = computed(() => {
  const map = new Map<number, number>()
  allImages.value.forEach((img, index) => {
    map.set(img.ImageId, index)
  })
  return map
})

/**
 * 5. 【防止水合失败】
 */
const activeCategory = ref(categories.value?.[0] || '')

watch(categories, (newCats) => {
  if (newCats.length > 0 && !activeCategory.value) {
    activeCategory.value = newCats[0] || ''
  }
})

/**
 * 6. 【DOM 引用管理】用于锚点恢复
 */
const imageRefs = ref<Map<number, HTMLElement>>(new Map())

const setImageRef = (el: HTMLElement | null, imageId: number) => {
  if (el) {
    imageRefs.value.set(imageId, el)
  } else {
    imageRefs.value.delete(imageId)
  }
}

/**
 * 7. 【滚动监听与分类跳转】
 */
let scrollTicking = false

const handleScrollCore = () => {
  scrollTicking = false
  if (import.meta.server) return

  const triggerPoint = 80 + window.innerHeight * 0.2

  for (const cat of categories.value) {
    const el = document.getElementById(`category-${cat}`)
    if (el) {
      const rect = el.getBoundingClientRect()
      if (rect.top <= triggerPoint && rect.bottom > triggerPoint) {
        if (activeCategory.value !== cat) {
          activeCategory.value = cat
        }
        break
      }
    }
  }
}

const handleScroll = () => {
  // 使用 RAF 节流，避免频繁触发布局计算
  if (!scrollTicking) {
    requestAnimationFrame(handleScrollCore)
    scrollTicking = true
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

/**
 * 【侧边栏数据】转换为通用格式
 */
const sidebarItems = computed<SidebarItem[]>(() => {
  return categories.value.map(cat => ({
    id: cat,
    text: cat
  }))
})

const sidebarSubtitle = computed(() => {
  return `共 ${wordCount.value} 个字，${imagesData.value?.total || 0} 张图片`
})

const selectCategory = (item: SidebarItem) => {
  activeCategory.value = item.id
  const element = document.getElementById(`category-${item.id}`)
  if (element) {
    const headerOffset = 80
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
  closeSidebar()
}

/**
 * 8. 【Viewer 状态管理】
 */
const viewerVisible = ref(false)
const viewerInitialIndex = ref(0)

/**
 * 打开 Viewer
 */
const openViewer = (image: GalleryImage) => {
  const index = imageIndexMap.value.get(image.ImageId)
  if (index !== undefined) {
    viewerInitialIndex.value = index
    viewerVisible.value = true
    
    // 预加载原图
    preloadOriginalImage(image.Key)
  }
}

/**
 * 关闭 Viewer 并恢复锚点
 */
const handleViewerClose = (currentIndex: number) => {
  viewerVisible.value = false
  
  // 获取当前查看的图片
  const currentImage = allImages.value[currentIndex]
  if (!currentImage) return
  
  // 恢复锚点
  nextTick(() => {
    const el = imageRefs.value.get(currentImage.ImageId)
    if (el) {
      el.scrollIntoView({
        block: 'center',
        behavior: 'auto'
      })
    }
  })
}

/**
 * 9. 【图片预加载】后台悄悄下载原图，使用全局缓存
 */
const preloadOriginalImage = (key: string) => {
  const url = getFullImageUrl(key)
  if (!isImageCached(url)) {
    preloadImage(url).catch(() => {
      // 静默处理加载失败
    })
  }
}

/**
 * 缩略图加载完成后预加载原图
 */
const handleThumbLoad = (key: string) => {
  // 使用 requestIdleCallback 在空闲时预加载原图
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => preloadOriginalImage(key), { timeout: 2000 })
  } else {
    setTimeout(() => preloadOriginalImage(key), 100)
  }
}

/**
 * 获取缩略图 URL
 */
const getImageUrl = (key: string) => getThumbUrl(key, 800)

// 处理 banner 点击
const handleBannerClick = () => {
  if (banner.value.Link) {
    if (import.meta.client) {
      window.open(banner.value.Link, '_blank')
    }
  }
}

// 加载状态
const isLoading = computed(() => 
  categoriesStatus.value === 'pending' || imagesStatus.value === 'pending'
)
</script>

<template>
  <div class="page-container">
    <!-- Hero 区域 -->
    <div class="gallery-hero">
      <img 
        :src="bannerImage" 
        :alt="banner.Title || 'Gallery Cover'" 
        class="hero-image" 
      />
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 class="hero-title">{{ banner.Title }}</h1>
        <p v-if="banner.Subtitle" class="hero-subtitle">{{ banner.Subtitle }}</p>
      </div>
      <div v-if="banner.Location" class="hero-location" :class="{ 'has-link': banner.Link }" @click="handleBannerClick">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        <span>{{ banner.Location }}</span>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="gallery-wrapper">
      <!-- 侧边栏 -->
      <div class="sidebar-wrapper">
        <UnifiedSidebar 
          mode="gallery"
          title="LiXiang的图片库"
          :subtitle="sidebarSubtitle"
          :items="sidebarItems"
          :active-id="activeCategory"
          :is-open="isSidebarOpen"
          @select="selectCategory"
          @close="closeSidebar"
        />
      </div>

      <!-- 画廊主体 -->
      <main class="gallery-content">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>正在探索图集...</p>
        </div>

        <!-- 空状态 -->
        <div v-else-if="categories.length === 0" class="empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" 
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <p>暂无相册分类</p>
        </div>

        <!-- 分类图片网格 -->
        <template v-else v-for="category in categories" :key="category">
          <section :id="`category-${category}`" class="category-section">
            <h2 class="category-header">
              <span class="category-name">{{ category }}</span>
              <span class="category-count">{{ imagesByCategory[category]?.length || 0 }} 张</span>
            </h2>
            
            <div class="image-grid">
              <div 
                v-for="img in imagesByCategory[category]" 
                :key="img.ImageId" 
                :ref="(el) => setImageRef(el as HTMLElement, img.ImageId)"
                class="image-item"
                @click="openViewer(img)"
              >
                <!-- 使用 BlurhashImage 组件 -->
                <BlurhashImage
                  v-if="img.BlurHash"
                  :src="getImageUrl(img.Key)"
                  :hash="img.BlurHash"
                  :alt="img.Title || ''"
                  @load="handleThumbLoad(img.Key)"
                />
                <!-- 无 BlurHash 时的降级处理 -->
                <img 
                  v-else
                  :src="getImageUrl(img.Key)" 
                  :alt="img.Title || ''" 
                  loading="lazy"
                  @load="handleThumbLoad(img.Key)"
                />
              </div>
              
              <p v-if="!imagesByCategory[category]?.length" class="no-images">
                该分类下还没有照片
              </p>
            </div>
          </section>
        </template>
      </main>
    </div>

    <!-- 图片查看器 -->
    <ImageViewer
      :images="allImages"
      :initial-index="viewerInitialIndex"
      :visible="viewerVisible"
      @close="handleViewerClose"
    />
  </div>
</template>

<style scoped>
.page-container {
  width: 100%;
}

/* Hero 区域 */
.gallery-hero {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  margin-bottom: 2rem;
  overflow: hidden;
  background-color: #1a1a1a;
}

@media (min-width: 768px) {
  .gallery-hero {
    aspect-ratio: 21/7;
  }
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.4) 100%
  );
}

.hero-content {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
}

.hero-title {
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(255, 255, 255, 0.9);
}

.hero-subtitle {
  margin: 0.5rem 0 0;
  font-size: 1rem;
  opacity: 0.9;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
}

.hero-location {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hero-location.has-link {
  cursor: pointer;
  transition: background-color 0.2s;
}

.hero-location.has-link:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* 主体布局 */
.gallery-wrapper {
  position: relative;
  display: flex;
}

.sidebar-wrapper {
  position: absolute;
  right: 100%;
  top: 0;
  padding-right: 3rem;
  height: 100%;
}

@media (max-width: 1536px) {
  .sidebar-wrapper {
    position: static;
    display: contents;
  }
}

.gallery-content {
  flex: 1;
  min-width: 0;
}

/* 分类区块 */
.category-section {
  margin-bottom: 4rem;
  scroll-margin-top: 100px;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  padding-left: 1rem;
  border-left: 4px solid currentColor;
}

.category-name {
  font-size: 1.25rem;
  font-weight: 600;
}

.category-count {
  font-size: 0.875rem;
  opacity: 0.5;
  font-weight: 400;
}

/* 图片网格 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.image-item {
  position: relative;
  aspect-ratio: 4/3;
  overflow: hidden;
  background-color: #f0f0f0;
  /* border-radius: 8px; */
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}



.image-item img,
.image-item :deep(.blurhash-container) {
  width: 100%;
  height: 100%;
}

.image-item :deep(.actual-image) {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease;
}


.no-images {
  grid-column: 1 / -1;
  text-align: center;
  color: #999;
  padding: 2rem;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
  color: #999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
  color: #999;
}

.empty-state svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* 响应式布局 */
@media (max-width: 1024px) {
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 1.75rem;
  }
  
  .hero-subtitle {
    font-size: 0.875rem;
  }
  
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  
  .category-section {
    margin-bottom: 3rem;
  }
  
  .category-header {
    padding-left: 0.75rem;
    border-left-width: 3px;
  }
  
  .category-name {
    font-size: 1.1rem;
  }
}

/* 深色模式 */
@media (prefers-color-scheme: dark) {
  .image-item {
    /* background-color: #2a2a2a; */
  }
  
  .loading-spinner {
    border-color: #444;
    border-top-color: #fff;
  }
}
</style>